import { FlatTransport } from '@/models/FlatTransport'

/** The most ids the flatTransports index takes in one filter[id] (API TransportIndexRequest::MAX_IDS). */
export const FLAT_TRANSPORT_IDS_PER_REQUEST = 500

/**
 * Loads FlatTransports by id, in the order of the ids. The V3 transports page hands its selection to
 * the V2 bulk dialogs as ids only (up to 5000). They are loaded with filter[id] on the flatTransports
 * index, `batchSize` ids per request (one page of that size), with at most `concurrency` requests in
 * flight. Ids the API does not return (not visible to the user, deleted) are left out.
 */
export async function fetchFlatTransportsById(
	ids: Array<string>,
	batchSize: number = FLAT_TRANSPORT_IDS_PER_REQUEST,
	concurrency: number = 2
): Promise<Array<FlatTransport>> {
	const batches: Array<Array<string>> = []
	for (let start = 0; start < ids.length; start += batchSize) {
		batches.push(ids.slice(start, start + batchSize))
	}

	const byId = new Map<string, FlatTransport>()
	let next = 0

	const worker = async (): Promise<void> => {
		while (next < batches.length) {
			const batch = batches[next++]
			const query = FlatTransport.where('id', batch.join(','))
			query.getQuery().getPaginationSpec().setPageLimit(batch.length)
			const transports = (await query.get(1)).getData() as Array<FlatTransport>
			transports.forEach((transport) => byId.set(String(transport.getApiId()), transport))
		}
	}

	await Promise.all(Array.from({ length: Math.min(concurrency, batches.length) }, worker))

	return ids.filter((id) => byId.has(id)).map((id) => byId.get(id) as FlatTransport)
}
